import React, { useState, useEffect, useCallback, useMemo } from 'react';
import CustomDateRangePicker from './CustomDateRangePicker';
import PeriodSelector from './PeriodSelector';

interface Period {
  label: string;
  value: string;
}

interface DateRange {
  start_date: string;
  end_date: string;
}

interface DateRangeChangePayload extends DateRange {
  period: string;
}

interface DateRangeSelectorProps {
  periods?: Period[];
  defaultPeriod?: string;
  onChange?: (range: DateRangeChangePayload) => void;
  storageKey?: string;
}

const createStorage = (key: string) => {
  const KEYS = {
    PERIOD: `${key}_period`,
    FROM: `${key}_custom_from`,
    TO: `${key}_custom_to`,
  };

  return {
    load: () => ({
      period: localStorage.getItem(KEYS.PERIOD) || null,
      customFrom: localStorage.getItem(KEYS.FROM) || '',
      customTo: localStorage.getItem(KEYS.TO) || '',
    }),
    save: (period: string, customFrom?: string, customTo?: string) => {
      localStorage.setItem(KEYS.PERIOD, period);

      if (period === 'custom') {
        if (customFrom) localStorage.setItem(KEYS.FROM, customFrom);
        if (customTo) localStorage.setItem(KEYS.TO, customTo);
      } else {
        localStorage.removeItem(KEYS.FROM);
        localStorage.removeItem(KEYS.TO);
      }
    },
  };
};

const DEFAULT_PERIODS: Period[] = [
  { label: 'Max', value: 'max' },
  { label: 'Year', value: 'year' },
  { label: 'Month', value: 'month' },
  { label: 'Week', value: 'week' },
  { label: 'Yesterday', value: 'yesterday' },
  { label: 'Today', value: 'today' },
  { label: 'Custom', value: 'custom' },
];

const pad = (value: number): string => String(value).padStart(2, '0');

const toLocalDateTimeString = (date: Date): string => {
  return [
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`,
    `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`,
  ].join('T');
};

const isValidDate = (date: Date): boolean => !Number.isNaN(date.getTime());

const isSameLocalDate = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

export const startOfDay = (date: Date = new Date()): Date => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

export const endOfDay = (date: Date = new Date()): Date => {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
};

const normalizeCustomPickerValue = (
  value: string,
  type: 'start' | 'end',
  previousValue: string,
): string => {
  if (!value) return '';

  const nextDate = new Date(value);

  if (!isValidDate(nextDate)) {
    const [datePart] = value.split('T');

    return type === 'end' ? `${datePart}T23:59:59` : `${datePart}T00:00:00`;
  }

  const previousDate = previousValue ? new Date(previousValue) : null;

  const isDateChanged =
    !previousDate || !isValidDate(previousDate) || !isSameLocalDate(previousDate, nextDate);

  if (isDateChanged) {
    type === 'end' ? nextDate.setHours(23, 59, 59, 999) : nextDate.setHours(0, 0, 0, 0);
  }

  return toLocalDateTimeString(nextDate);
};

const normalizeCustomDateTime = (value: string, type: 'start' | 'end'): Date => {
  const [datePart, timePart] = value.split('T');
  const defaultTime = type === 'end' ? '23:59:59' : '00:00:00';
  const normalizedTime = timePart
    ? timePart.length === 5
      ? `${timePart}:00`
      : timePart
    : defaultTime;

  const date = new Date(`${datePart}T${normalizedTime}`);
  if (type === 'end') date.setMilliseconds(999);
  return date;
};

const PERIOD_HANDLERS: Record<string, () => Date> = {
  max: () => new Date(Date.UTC(2000, 0, 1)),
  year: () => {
    const d = new Date();
    return new Date(Date.UTC(d.getFullYear(), 0, 1));
  },
  month: () => {
    const d = new Date();
    return new Date(Date.UTC(d.getFullYear(), d.getMonth(), 1));
  },
  week: () => {
    const now = new Date();
    const d = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
    const day = d.getUTCDay();
    const diff = day === 0 ? 6 : day - 1;
    d.setUTCDate(d.getUTCDate() - diff);
    return d;
  },
  yesterday: () => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  },
  today: () => {
    const d = new Date();
    return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  },
};

export const getDateRangeByPeriod = (period: string): DateRange | null => {
  if (period === 'custom') return null;

  const handler = PERIOD_HANDLERS[period];
  if (!handler) return null;

  const startDate = handler();
  const endDate = period === 'yesterday' ? endOfDay(startDate) : null;

  return {
    start_date: startDate.toISOString(),
    end_date: endDate?.toISOString() ?? null,
  };
};

export default function DateRangeSelector({
  periods = DEFAULT_PERIODS,
  defaultPeriod = 'today',
  onChange,
  storageKey,
}: DateRangeSelectorProps): React.ReactElement {
  const storage = useMemo(() => (storageKey ? createStorage(storageKey) : null), [storageKey]);

  const [period, setPeriod] = useState<string>(() => storage?.load().period ?? defaultPeriod);
  const [customFrom, setCustomFrom] = useState<string>(() => storage?.load().customFrom ?? '');
  const [customTo, setCustomTo] = useState<string>(() => storage?.load().customTo ?? '');

  const handleCustomFromChange: React.Dispatch<React.SetStateAction<string>> = (value) => {
    setCustomFrom((previousValue) => {
      const nextValue = typeof value === 'function' ? value(previousValue) : value;

      return normalizeCustomPickerValue(nextValue, 'start', previousValue);
    });
  };

  const handleCustomToChange: React.Dispatch<React.SetStateAction<string>> = (value) => {
    setCustomTo((previousValue) => {
      const nextValue = typeof value === 'function' ? value(previousValue) : value;

      return normalizeCustomPickerValue(nextValue, 'end', previousValue);
    });
  };

  const handlePeriodChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const next = e.target.value;
      setPeriod(next);
      storage?.save(next);
    },
    [storage],
  );

  useEffect(() => {
    if (period === 'custom') {
      if (customFrom && customTo) {
        storage?.save(period, customFrom, customTo);
        const start = normalizeCustomDateTime(customFrom, 'start');
        const end = normalizeCustomDateTime(customTo, 'end');

        onChange?.({
          period,
          start_date: start.toISOString(),
          end_date: end.toISOString(),
        });
      }
      return;
    }

    const range = getDateRangeByPeriod(period);
    if (range) {
      onChange?.({ period, ...range });
    }
  }, [period, customFrom, customTo, onChange]);

  return (
    <div>
      <PeriodSelector selectedPeriod={period} onChange={handlePeriodChange} periods={periods} />
      {period === 'custom' && (
        <CustomDateRangePicker
          customFrom={customFrom}
          customTo={customTo}
          setCustomFrom={handleCustomFromChange}
          setCustomTo={handleCustomToChange}
        />
      )}
    </div>
  );
}
